import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { reduxForm } from 'redux-form';
import { capitalize as _capitalize } from 'lodash';
import { scrollToElementIfNotVisible } from '@saucelabs/sl-util-helpers';
import { records as userRecords } from '@saucelabs/sl-module-user';
import Alert from '@saucelabs/sl-component-alert';
import { DeviceGrid } from '@saucelabs/sl-component-device-grid';
import { DebouncedInput } from '@saucelabs/sl-component-form';
import { ContentLoader, Loader } from '@saucelabs/sl-component-loader';
import { Row, Col } from '@saucelabs/sl-component-grid';
import { constants as deviceRdcConstants } from '@saucelabs/sl-module-devices-rdc';

import { TESTOBJECT_FASTSWITCH_URL } from '../../constants';
import * as actions from '../../actions';
import Styles from './RealDevicesContainer.styles';
import RdcErrorPage from '../sessionReal/RdcErrorPage';
import CheckboxGroup from '../../components/CheckboxGroup';
import RadioGroup from '../../components/RadioGroup';
import RealDevicesConfiguratorTunnel from '../../components/RealDevicesConfiguratorTunnel';
import RealDevicesFiltersPanel from '../../components/RealDevicesFilters/RealDevicesFiltersPanel';
import RealDevicesGridItem from '../../components/RealDevicesGridItem';

const mapLabel = (functor) => (option) => ({
  label: functor(option),
  value: option,
});

const mapLabels = (functor) => (options) => options.map(mapLabel(functor));

const getCheckboxOptions = mapLabels((id) => id);
const getScreenSizeCheckboxOptions = mapLabels((option) => `${option}"`);
const getCapitalizedCheckboxOptions = mapLabels((option) => _capitalize(option));
const getOSCheckboxOptions = mapLabels((option) => (option === 'IOS' ? 'iOS' : 'Android'));
const getNetworkTypeCheckboxOptions = mapLabels((option) => `${_capitalize(option)} Network`);


class RealDevices extends Component {
  componentDidMount() {
    const {
      dataCenterId,
      os,
      user,
      getDeviceGridInitialData,
    } = this.props;

    const userData = {
      ancestor: user.ancestor,
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
    };

    getDeviceGridInitialData({
      user: userData,
      os,
      dataCenterId,
    });
  }

  componentDidUpdate(prevProps) {
    const {
      devices,
      deviceConfiguration,
      loading,
    } = this.props;

    if (!loading && devices.length && deviceConfiguration.compositeId) {
      scrollToElementIfNotVisible(deviceConfiguration.compositeId, 'deviceGrid', {}, !prevProps.loading);
    }
  }

  componentWillUnmount() {
    const { unloadDeviceGrid } = this.props;

    unloadDeviceGrid();
  }

  renderDevice = (device) => {
    const {
      closeTileDetails,
      detailsOpenDeviceId,
      deviceConfiguration,
      onDeviceSelect,
      openTileDetails,
    } = this.props;

    const selected = deviceConfiguration.compositeId === device.compositeId;
    const handleClick = () => onDeviceSelect(device);
    const handleDetailsClick = () => openTileDetails(device.compositeId);
    const detailsOpen = detailsOpenDeviceId === device.compositeId;

    return (
      <RealDevicesGridItem
        key={device.compositeId}
        closeTileDetails={closeTileDetails}
        detailsOpen={detailsOpen}
        device={device}
        handleClick={handleClick}
        handleDetailsClick={handleDetailsClick}
        selected={selected}
      />
    );
  }

  render() {
    const {
      change,
      devices,
      facets,
      isFreeRdcUser,
      isRdcTrialEnded,
      loading,
      meta,
      onTunnelChange,
      os,
      rdcTunnels,
      dataCenterId,
      userRdcId,
    } = this.props;

    const manufacturerSelectionEnabled = os !== deviceRdcConstants.MOBILE_PLATFORMS.IOS;
    const platformSelectionEnabled = !os;

    return (
      <RdcErrorPage>
        <ContentLoader show={!userRdcId}>
          <div className={Styles.default}>
            {isFreeRdcUser && (
              <Alert bsStyle="danger">
                {isRdcTrialEnded ? 'Your trial period has ended. ' : 'Get access to all devices. '}
                <a
                  className={Styles['upgrade-link']}
                  href={`${TESTOBJECT_FASTSWITCH_URL}?next=/${userRdcId}/account/plans`}
                >
                  Upgrade now...
                </a>
              </Alert>
            )}
            {meta.error && meta.submitFailed && <Alert bsStyle="danger">You have to choose a device</Alert>}
            <Row className={Styles.content}>
              {facets && (
                <Col
                  className={Styles['filters-column']}
                  md
                  xs={12}
                >
                  {devices.length > 0 && (
                    <span className={Styles['device-count']}>
                      {devices.length}
                      {' Device Model'}
                      {devices.length > 1 && 's'}
                    </span>
                  )}
                  {facets.dataCenter && (
                    <RealDevicesFiltersPanel
                      defaultExpanded
                      header="Location"
                    >
                      <RadioGroup
                        name="dataCenterId"
                        options={getCheckboxOptions(
                          Object.keys(deviceRdcConstants.MOBILE_DATA_CENTER_NAMES),
                        )}
                      />
                    </RealDevicesFiltersPanel>
                  )}
                  {platformSelectionEnabled && facets.os && (
                    <RealDevicesFiltersPanel
                      defaultExpanded
                      header="Platform"
                    >
                      <CheckboxGroup
                        name="os"
                        options={getOSCheckboxOptions(facets.os)}
                      />
                    </RealDevicesFiltersPanel>
                  )}
                  {facets.cloudType && (
                    <RealDevicesFiltersPanel
                      defaultExpanded
                      header="Cloud Type"
                    >
                      <CheckboxGroup
                        name="cloudType"
                        options={getCapitalizedCheckboxOptions(facets.cloudType)}
                      />
                    </RealDevicesFiltersPanel>
                  )}
                  {facets.osVersion && (
                    <RealDevicesFiltersPanel
                      defaultExpanded
                      header="OS Version"
                    >
                      <CheckboxGroup
                        name="osVersion"
                        options={getCheckboxOptions(facets.osVersion)}
                      />
                    </RealDevicesFiltersPanel>
                  )}
                  {manufacturerSelectionEnabled && facets.manufacturer && (
                    <RealDevicesFiltersPanel
                      defaultExpanded
                      header="Manufacturer"
                    >
                      <CheckboxGroup
                        name="manufacturer"
                        options={getCheckboxOptions(facets.manufacturer)}
                      />
                    </RealDevicesFiltersPanel>
                  )}
                  {facets.networkType && (
                    <RealDevicesFiltersPanel header="Network Type">
                      <CheckboxGroup
                        name="networkType"
                        options={getNetworkTypeCheckboxOptions(facets.networkType)}
                      />
                    </RealDevicesFiltersPanel>
                  )}
                  {facets.formFactor && (
                    <RealDevicesFiltersPanel header="Device Type">
                      <CheckboxGroup
                        name="formFactor"
                        options={getCapitalizedCheckboxOptions(facets.formFactor)}
                      />
                    </RealDevicesFiltersPanel>
                  )}
                  {facets.resolution && (
                    <RealDevicesFiltersPanel header="Resolution">
                      <CheckboxGroup
                        name="resolution"
                        options={getCheckboxOptions(facets.resolution)}
                      />
                    </RealDevicesFiltersPanel>
                  )}
                  {facets.screenSize && (
                    <RealDevicesFiltersPanel header="Screen Size">
                      <CheckboxGroup
                        name="screenSize"
                        options={getScreenSizeCheckboxOptions(facets.screenSize)}
                      />
                    </RealDevicesFiltersPanel>
                  )}
                  {facets.dpi && (
                    <RealDevicesFiltersPanel header="DPI">
                      <CheckboxGroup
                        name="dpi"
                        options={getCheckboxOptions(facets.dpi)}
                      />
                    </RealDevicesFiltersPanel>
                  )}
                </Col>
              )}
              <Col
                className={Styles['results-column']}
                md
              >
                <Loader
                  className={Styles.loader}
                  fontSize={3}
                  show={!facets}
                >
                  <div className={Styles['results-column-content']}>
                    <div>
                      <Row>
                        <Col
                          lg
                          sm
                          xs={12}
                        >
                          <DebouncedInput
                            debounceMs={300}
                            handleChange={(value) => change('search', value)}
                            label="Search devices"
                            minWidth={3}
                          />
                        </Col>
                        <Col
                          className={Styles.default}
                          lg
                          sm
                          xs={12}
                        >
                          <RealDevicesConfiguratorTunnel
                            activeDataCenterId={dataCenterId}
                            onChange={onTunnelChange}
                            tunnels={rdcTunnels.toJS()}
                          />
                        </Col>
                      </Row>
                    </div>
                    <Loader
                      className={Styles.loader}
                      fontSize={3}
                      show={loading}
                    >
                      <p>
                        {'Please select your device. Can\'t find your device listed? '}
                        <a
                          href="https://goo.gl/forms/Hp5cGKMqGILz0xLx1"
                          rel="noreferrer noopener"
                          target="_blank"
                        >
                          Let us know!
                        </a>
                      </p>
                      <DeviceGrid id="deviceGrid">
                        {devices.map(this.renderDevice)}
                      </DeviceGrid>
                    </Loader>
                  </div>
                </Loader>
              </Col>
            </Row>
          </div>
        </ContentLoader>
      </RdcErrorPage>
    );
  }
}

const mapStateToProps = ({ apps: { manual }, rdcTunnels, user, userRdc }) => ({
  dataCenterId: manual.rdcDevicePicker.get('lastDataCenterId'),
  detailsOpenDeviceId: manual.devices.get('detailsOpenDeviceId'),
  devices: manual.devices.get('items').toArray(),
  initialValues: {
    dataCenterId: manual.rdcDevicePicker.get('lastDataCenterId'),
  },
  facets: manual.devices.get('facets'),
  isFreeRdcUser: userRdc.get('subscription').isFreeUser(),
  isRdcTrialEnded: userRdc.get('subscription').isTrialEnded(),
  loading: manual.devices.get('loading'),
  user,
  userRdcId: userRdc.get('id'),
  rdcTunnels,
});

const mapDispatchToProps = {
  cleanupMobileDevicesFacets: actions.cleanupMobileDevicesFacets,
  closeTileDetails: actions.closeMobileDeviceTileDetails,
  fetchAvailableDeviceIds: actions.fetchAvailableDeviceIds,
  fetchMobileDevices: actions.fetchMobileDevices,
  getDeviceGridInitialData: actions.getDeviceGridInitialData,
  openTileDetails: actions.openMobileDeviceTileDetails,
  selectDataCenter: actions.selectDataCenter,
  unloadDeviceGrid: actions.unloadDeviceGrid,
};

RealDevices.propTypes = {
  change: PropTypes.func.isRequired,
  closeTileDetails: PropTypes.func.isRequired,
  dataCenterId: PropTypes.string.isRequired,
  detailsOpenDeviceId: PropTypes.string,
  deviceConfiguration: PropTypes.object.isRequired,
  devices: PropTypes.array.isRequired,
  facets: PropTypes.object,
  getDeviceGridInitialData: PropTypes.func.isRequired,
  isFreeRdcUser: PropTypes.bool.isRequired,
  isRdcTrialEnded: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  meta: PropTypes.object.isRequired,
  onDeviceSelect: PropTypes.func.isRequired,
  onTunnelChange: PropTypes.func.isRequired,
  openTileDetails: PropTypes.func.isRequired,
  os: PropTypes.string,
  rdcTunnels: PropTypes.object.isRequired,
  unloadDeviceGrid: PropTypes.func.isRequired,
  user: ImmutablePropTypes.recordOf(userRecords.UserRecordClass).isRequired,
  userRdcId: PropTypes.string,
};

RealDevices.defaultProps = {
  detailsOpenDeviceId: undefined,
  facets: null,
  os: undefined,
  userRdcId: null,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(reduxForm({
  form: 'manualRealDevices',
  destroyOnUnmount: false,
  onChange: (values, dispatch, props) => {
    if (values.dataCenterId !== props.dataCenterId) {
      props.selectDataCenter(values.dataCenterId);
      props.fetchAvailableDeviceIds(values.dataCenterId);
    }
    return props.fetchMobileDevices({ os: [props.os], ...values });
  },
})(RealDevices));
export { RealDevices, mapStateToProps, mapDispatchToProps };
